/****************************************************************************
 * COPYRIGHT (c) 2024 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/
import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";

import harmanlogo from "../../assets/images/logo.png";
import "./header.css";
import {
  ListItemIcon,
  Menu,
  MenuItem,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  CircularProgress,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import LogoutIcon from "@mui/icons-material/Logout";
import ThreePIcon from "@mui/icons-material/ThreeP";
import { fetchIdsDetails } from "../../Services/redux/Ids/IdsActions";
import { useDispatch, useSelector } from "react-redux";
import { getConfig } from "../../config";

const PROFILE_ROUTES = {
  "oem-admin": "/ui/oem-admin/user-profile",
  "domain-approver": "/ui/domain-approver/user-profile",
  "seller": "/ui/developer/myprofile",
  "global-admin": "/ui/harman-admin/user-profile",
};

const DEVELOPER_TYPE_MAP = {
  "oem-admin": "OEM Admin",
  "seller": "Developer",
  "global-admin": "Admin",
  "domain-approver": "Domain-Approver",
};

export default function Topheader() {
  const getPortfolioIdValue = (portfolio) => {
    if (portfolio && typeof portfolio === "object") {
      return portfolio.id || "";
    }
    return portfolio || "";
  };

  const [posting, setPosting] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const url = "https://harman-ignite-store.zendesk.com/access/jwt?return_to=https://harman-ignite-store.zendesk.com/hc/en-us/requests";
  const formData = { jwt: "" };
  const dispatch = useDispatch();
  const idsData = useSelector((state) => state.idsDetails.ids);
  const idsLoading = useSelector((state) => state.idsDetails.loading);
  const [selectedId, setSelectedId] = useState("");
  const userRole = getConfig().REACT_APP_PORTAL;
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingPortfolioId, setPendingPortfolioId] = useState("");
  const zendeskEnabled = getConfig().ZENDESK_ENABLE_LOGIN;
  const open = Boolean(anchorEl);
  const customPortal = sessionStorage.getItem("customPortal");
  const [clientId, setClientId] = useState("");
  const [scopeId, setScopeId] = useState("");

  useEffect(() => {
    if (customPortal === "oem-admin") {
      // Only fetch if data doesn't exist and not currently loading
      if (
        (!idsData || (Array.isArray(idsData) && idsData.length === 0)) &&
        !idsLoading
      ) {
        dispatch(fetchIdsDetails());
      }
    }
  }, [dispatch, idsData, idsLoading, customPortal]);

  const tabOptions = {
    OEMadmin: [
      { name: "Overview", route: "/ui/oem-admin/dashboard" },
      { name: "Applications", route: "/ui/oem-admin/applications" },

      // { name: "Features", route: "/ui/admin/features" },
      { name: "Analytics", route: "/ui/developer/analytics" },
      { name: "Settings", route: "/ui/oem-admin/settings" },
    ],
    seller: [
      { name: "Applications", route: "/ui/developer/applications" },
      { name: "Codelab", route: "/ui/developer/codelab" },
      { name: "Documentation", route: "/ui/developer/documentation" },
      { name: "Tools", route: "/ui/developer/tools" },
    ],
    oemSeller: [
      { name: "Applications", route: "/ui/developer/applications" },
      { name: "Codelab", route: "/ui/developer/codelab" },
      { name: "Documentation", route: "/ui/developer/documentation" },
      { name: "Tools", route: "/ui/developer/tools" },
    ],
    vwLoginSeller: [
      { name: "Applications", route: "/ui/developer/applications" },
      //{ name: "Features", isActive: false, route: "" },
      { name: "Help", route: "/ui/developer/help" },
    ],
    domainApprover: [
      { name: "Applications", route: "/ui/domain-approver/applications" },
      {
        name: "Portfolio Settings",
        route: "/ui/domain-approver/portfolio-settings",
      },
    ],
    globalAdmin: [
      { name: "Dashboard", route: "/ui/harman-admin/dashboard" },
      { name: "Applications", route: "/ui/harman-admin/applications" },
      { name: "Users", route: "/ui/harman-admin/users" },
      { name: "Settings", route: "/ui/harman-admin/categories" },
      { name: "Reports", route: "/ui/harman-admin/admin-reports" },
    ],
  };
  const [tabName, setTabName] = useState(tabOptions[userRole]);
  const navigate = useNavigate();

  function handleProfileClick(event) {
    setAnchorEl(event.currentTarget);
  }

  function handleClose() {
    setAnchorEl(null);
  }

  function handleRouteToUserProfile() {
    const route = PROFILE_ROUTES[getConfig().REACT_APP_PORTAL];
    if (route) navigate(route);
  }

  async function redirectToZendesk() {
     const access_token = sessionStorage.getItem("access_token");
     const id_token = sessionStorage.getItem("id_token");
     const BE_gateway_url = getConfig().REACT_APP_BACKEND_URL;
     const FE_gateway_url = getConfig().REACT_APP_FRONTEND_URL;
     const portal = getConfig().REACT_APP_PORTAL;
     console.log("Initiating redirect to Zendesk with portal:", portal);
     setPosting(true);

    try {
      const zendeskJwtUrl =
        portal === "oem-admin"
          ? `${FE_gateway_url}/v1/zendesk/jwt`
          : `${BE_gateway_url}/v1/zendesk/jwt`;

      console.log("Calling Zendesk JWT API at:", zendeskJwtUrl);

      const apiResponse = await fetch(zendeskJwtUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization":  portal === "oem-admin" ?  `Bearer ${id_token}` : `Bearer ${access_token}` ,
        },
      });

      // Check if API call was successful
      if (!apiResponse.ok) {
        console.error("Zendesk API call failed with status:", apiResponse.status);
        copyEmailToClipboard();
        setPosting(false);
        return;
      }

      const contentType = apiResponse.headers.get("content-type") || "";
      const responseData = contentType.includes("application/json")
        ? await apiResponse.json()
        : await apiResponse.text();
      console.log("Zendesk token API response:", responseData);

      // Get the JWT token from the response
      const jwtToken = typeof responseData === 'object' ? (responseData.jwt || responseData.token) : responseData;
      console.log("Extracted JWT token:", jwtToken);
      if (jwtToken) {
        // Create a form and redirect to Zendesk in a new tab
        const form = document.createElement("form");
        form.action = url;
        form.method = "POST";

        for (const key in formData) {
          if (formData.hasOwnProperty(key)) {
            const input = document.createElement("input");
            input.type = "hidden";
            input.name = key;
            input.value = jwtToken;
            form.appendChild(input);
          }
        }

        document.body.appendChild(form);
        form.submit();
        form.remove();
      } else {
        // No JWT token received, fallback to email
        copyEmailToClipboard();
      }
    } catch (error) {
      console.error("Error calling zendesk-token API:", error);
      copyEmailToClipboard();
    } finally {
      setPosting(false);
    }
  }
  

  const handleLogout = async () => {
    const app_portal = getConfig().REACT_APP_PORTAL;
    if (
      sessionStorage.getItem("idpLogin") &&
      sessionStorage.getItem("idpLogin") === "false"
    ) {
      const cognitoDomain = getConfig().REACT_APP_COGNITO_DOMAIN;
      const redirect_uri = getConfig().REACT_APP_REDIRECT_URI;
      const logoutUrl = `${cognitoDomain}/logout?client_id=${clientId}&response_type=code&scope=${scopeId}&redirect_uri=${redirect_uri}&portal=${app_portal}`;
      sessionStorage.clear();
      globalThis.location.href = logoutUrl;
    } else {
      const revokeURL = `${getConfig().REACT_APP_COGNITO_DOMAIN}/oauth2/revoke`;
      try {
        const body = new URLSearchParams();
        body.set("token", sessionStorage.getItem("refresh_token"));
        body.set("client_id", clientId);

        const response = await fetch(revokeURL, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: body.toString(),
        });
        if (response.ok) {
          const redirect_uri = getConfig().REACT_APP_REDIRECT_URI;
          let logoutUrl = `${
            getConfig().REACT_APP_VW_LOGOUT_URL
          }${sessionStorage.getItem(
            "IDP_TOKEN"
          )}&post_logout_redirect_uri=${redirect_uri}&state=logout_state_1`;
          sessionStorage.clear();
          sessionStorage.setItem("idpLogout", true);
          globalThis.location.href = logoutUrl;
        }
      } catch (error) {
        console.error("Error during logout:", error);
      }
    }
  };

  function onPost($event) {
    $event.stopPropagation();
    $event.preventDefault();
    if (!posting) {
      redirectToZendesk();
    }
  }

  const copyEmailToClipboard = () => {
    const email = "support@harman-ignite-store.zendesk.com";
    navigator.clipboard.writeText(email).then(() => {
      alert("Zendesk is down, our technical team is working to restore ASAP.\nPlease send out the mail to Harman Support " + email + " to address your issues.\n\nEmail copied to clipboard!");
    });
  };

  let changeActive = (index) => {
    sessionStorage.removeItem("currentLanguage");
    if (!sessionStorage.getItem("invalidUser")) {
      setTabName(tabName.map((tab, indx) => ({ ...tab, isActive: indx === index })));
    }
  };
  useEffect(() => {
    if (idsData && idsData.length > 0) {
      // Check if there's already a portfolio ID in sessionStorage
      const existingPortfolioId = sessionStorage.getItem("portfolioId");

      if (existingPortfolioId && idsData.includes(existingPortfolioId)) {
        // If existing portfolio ID exists and is valid, use it
        setSelectedId(existingPortfolioId);
      } else {
        // Otherwise, set to first item and update sessionStorage
        setSelectedId(idsData[0]);
        sessionStorage.setItem("portfolioId", idsData[0]);
      }

      if (sessionStorage.getItem("portfolioId") === null) {
        sessionStorage.setItem("portfolioId", idsData[0]);
      }
    }
  }, [idsData]);


  useEffect(() => {
    const app_portal = getConfig().REACT_APP_PORTAL;
    const idpLogin = sessionStorage.getItem("idpLogin");
    const isOemSeller = sessionStorage.getItem("isOemSeller");
    const isOemAdmin = app_portal; // This will be "oemadmin" or "seller"
    if (isOemAdmin === "oem-admin") {
      setTabName(tabOptions["OEMadmin"]);
    } else if (isOemAdmin === "domain-approver") {
      setTabName(tabOptions["domainApprover"]);
    } else if (isOemAdmin === "global-admin" && idpLogin !== "true") {
      setTabName(tabOptions["globalAdmin"]);
    } else if (isOemSeller && idpLogin !== "true") {
      setTabName(tabOptions["oemSeller"]);
    } else if (idpLogin === "true") {
      setTabName(tabOptions["vwLoginSeller"]);
    } else {
      setTabName(tabOptions["seller"]);
    }
  }, []);
  const handleChange = (event) => {
    const newPortfolioId = event.target.value;

    // Store the previous portfolio ID in case user cancels
    setPendingPortfolioId(selectedId);

    // Update the dropdown immediately to show selected value
    setSelectedId(newPortfolioId);

    // Open confirmation dialog
    setConfirmDialogOpen(true);
  };

  const handleConfirmPortfolioChange = () => {
    const newPortfolioId = getPortfolioIdValue(selectedId);
    const previousPortfolioId = getPortfolioIdValue(pendingPortfolioId);

    // Update sessionStorage with new portfolio ID
    sessionStorage.setItem("portfolioId", newPortfolioId);

    // Manually trigger storage event for same-page portfolio changes
    globalThis.dispatchEvent(
      new StorageEvent("storage", {
        key: "portfolioId",
        newValue: newPortfolioId,
        oldValue: previousPortfolioId,
        storageArea: sessionStorage,
      })
    );

    // Navigate to Applications page
    navigate("/ui/oem-admin/applications");

    // Close the dialog
    setConfirmDialogOpen(false);
    setPendingPortfolioId("");
  };

  const handleCancelPortfolioChange = () => {
    // Revert back to the previous portfolio ID
    setSelectedId(pendingPortfolioId);
    setConfirmDialogOpen(false);
    setPendingPortfolioId("");
  };
  const developerType = DEVELOPER_TYPE_MAP[getConfig().REACT_APP_PORTAL];
  useEffect(() => {
    const app_portal = getConfig().REACT_APP_PORTAL;
    if (app_portal === "oem-admin") {
      setScopeId(
        "rs%2FigniteStorePortfolioManager%20openid%20aws.cognito.signin.user.admin"
      );
    } else if (app_portal === "seller") {
      setScopeId(
        "rs%2FigniteStoreSeller%20openid%20aws.cognito.signin.user.admin"
      );
    } else if (app_portal === "domain-approver") {
      setScopeId(
        "rs%2FigniteStoreDomainApprover%20openid%20aws.cognito.signin.user.admin"
      );
    } else if (app_portal === "global-admin") {
      setScopeId(
        "rs%2FigniteStoreAdmin%20openid%20aws.cognito.signin.user.admin"
      );
    }
  }, []);

  useEffect(() => {
    const app_portal = getConfig().REACT_APP_PORTAL;
    if (app_portal === "oem-admin") {
      if (sessionStorage.getItem("idpLogin") === "true") {
        setClientId(getConfig().REACT_APP_VW_OEM_ADMIN_CLIENT_ID);
      } else {
        setClientId(getConfig().REACT_APP_OEM_ADMIN_CLIENT_ID);
      }
    } else if (app_portal === "seller") {
      if (sessionStorage.getItem("idpLogin") === "true") {
        setClientId(getConfig().REACT_APP_VW_CLIENT_ID);
      } else {
        setClientId(getConfig().REACT_APP_CLIENT_ID);
      }
    } else if (app_portal === "domain-approver") {
      if (sessionStorage.getItem("idpLogin") === "true") {
        setClientId(getConfig().REACT_APP_VW_DOMAIN_APPROVER_CLIENT_ID);
      } else {
        setClientId(getConfig().REACT_APP_DOMAIN_APPROVER_CLIENT_ID);
      }
    }
    else if (app_portal === "global-admin") {
      setClientId(getConfig().REACT_APP_GLOBAL_ADMIN_CLIENT_ID);
    }
  }, []);

  let tooltipTitle;
  if (posting) {
    tooltipTitle = "Loading...";
  } else if (zendeskEnabled) {
    tooltipTitle = "Zendesk";
  } else {
    tooltipTitle = (
      <span>
        Zendesk is down, our technical team is working to restore ASAP.<br />
        Please send out the mail to Harman Support <b>support@harman-ignite-store.zendesk.com</b> to address your issues.<br />
        <i>Click to copy the email</i>
      </span>
    );
  }

  return (
    !sessionStorage.getItem("invalidUser") &&
    !sessionStorage.getItem("pendingApproval") && (
      <header>
        <nav className="navbar">
          <div
            className="navbar-left"
            style={{
              width:
                getConfig().REACT_APP_PORTAL === "oem-admin"
                  ? "420px"
                  : "300px",
            }}
          >
            <img src={harmanlogo} alt="harman-logo" width={"195px"} />

            <div className="text-console">
              
              <span className="dot">.</span>
              <span className="developer" style={{ textWrap: "nowrap" }}>
                {developerType}
              </span>
              <span className="console">Console</span>
            </div>
          </div>
          <div className="navbar-middle">
            {tabName?.map((tab, index) => {
              const isAgreementPending = sessionStorage.getItem("agreementPending") === "true";
              const isDocumentationTab = tab.name === "Documentation";
              const isDisabled = isAgreementPending && !isDocumentationTab;
              return (
              <div className="headerNavActionCls" key={tab.route}>
                <NavLink
                  onClick={(e) => {
                    if (isDisabled) {
                      e.preventDefault();
                      return;
                    }
                    changeActive(index);
                  }}
                  to={tab.route}
                  className={tab.isActive ? "padding-det" : "not-bold"}
                  style={isDisabled ? { cursor: "not-allowed", opacity: 0.5 } : {}}
                >
                  {tab.name}
                </NavLink>
              </div>
              );
            })}
          </div>

          <div className="navbar-right">
            <Tooltip 
              title={tooltipTitle}
              slotProps={{
                tooltip: {
                  sx: {
                    fontSize: zendeskEnabled ? "inherit" : "14px",
                    maxWidth: "350px",
                    padding: "10px 12px",
                    lineHeight: "1.5",
                  },
                },
              }}
            >
              {posting ? (
                <CircularProgress size={24} sx={{ color: "white" }} />
              ) : (
                <ThreePIcon
                  sx={{
                    color: zendeskEnabled ? "white" : "gray",
                    cursor: "pointer",
                    opacity: zendeskEnabled ? 1 : 0.5,
                  }}
                  onClick={zendeskEnabled ? onPost : copyEmailToClipboard}
                ></ThreePIcon>
              )}
            </Tooltip>
            {getConfig().REACT_APP_PORTAL === "oem-admin" && (
              <div>
                <select
                  id="idsDropdown"
                  value={getPortfolioIdValue(selectedId)}
                  onChange={handleChange}
                  key={selectedId}
                >
                  {idsData && idsData.length > 0 ? (
                    idsData.map((id) => (
                      <option key={getPortfolioIdValue(id)} value={getPortfolioIdValue(id)}>
                        {getPortfolioIdValue(id)}
                      </option>
                    ))
                  ) : (
                    <option disabled>Loading...</option>
                  )}
                </select>
              </div>
            )}

            <Tooltip title="User Profile">
              <svg
                style={{ cursor: "pointer" }}
                width="32"
                height="32"
                viewBox="0 0 32 32"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                onClick={handleProfileClick}
              >
                <path
                  d="M0 4C0 1.79086 1.79086 0 4 0H28C30.2091 0 32 1.79086 32 4V28C32 30.2091 30.2091 32 28 32H4C1.79086 32 0 30.2091 0 28V4Z"
                  fill="white"
                />
                <path
                  d="M13.6 17.8C13.32 17.8 13.0833 17.7033 12.89 17.51C12.6967 17.3167 12.6 17.08 12.6 16.8C12.6 16.52 12.6967 16.2833 12.89 16.09C13.0833 15.8967 13.32 15.8 13.6 15.8C13.88 15.8 14.1167 15.8967 14.31 16.09C14.5033 16.2833 14.6 16.52 14.6 16.8C14.6 17.08 14.5033 17.3167 14.31 17.51C14.1167 17.7033 13.88 17.8 13.6 17.8ZM18.4 17.8C18.12 17.8 17.8833 17.7033 17.69 17.51C17.4967 17.3167 17.4 17.08 17.4 16.8C17.4 16.52 17.4967 16.2833 17.69 16.09C17.8833 15.8967 18.12 15.8 18.4 15.8C18.68 15.8 18.9167 15.8967 19.11 16.09C19.3033 16.2833 19.4 16.52 19.4 16.8C19.4 17.08 19.3033 17.3167 19.11 17.51C18.9167 17.7033 18.68 17.8 18.4 17.8ZM16 22.4C17.7867 22.4 19.3 21.78 20.54 20.54C21.78 19.3 22.4 17.7867 22.4 16C22.4 15.68 22.38 15.37 22.34 15.07C22.3 14.77 22.2267 14.48 22.12 14.2C21.84 14.2667 21.56 14.3167 21.28 14.35C21 14.3833 20.7067 14.4 20.4 14.4C19.1867 14.4 18.04 14.14 16.96 13.62C15.88 13.1 14.96 12.3733 14.2 11.44C13.7733 12.48 13.1633 13.3833 12.37 14.15C11.5767 14.9167 10.6533 15.4933 9.6 15.88V16C9.6 17.7867 10.22 19.3 11.46 20.54C12.7 21.78 14.2133 22.4 16 22.4ZM16 24C14.8933 24 13.8533 23.79 12.88 23.37C11.9067 22.95 11.06 22.38 10.34 21.66C9.62 20.94 9.05 20.0933 8.63 19.12C8.21 18.1467 8 17.1067 8 16C8 14.8933 8.21 13.8533 8.63 12.88C9.05 11.9067 9.62 11.06 10.34 10.34C11.06 9.62 11.9067 9.05 12.88 8.63C13.8533 8.21 14.8933 8 16 8C17.1067 8 18.1467 8.21 19.12 8.63C20.0933 9.05 20.94 9.62 21.66 10.34C22.38 11.06 22.95 11.9067 23.37 12.88C23.79 13.8533 24 14.8933 24 16C24 17.1067 23.79 18.1467 23.37 19.12C22.95 20.0933 22.38 20.94 21.66 21.66C20.94 22.38 20.0933 22.95 19.12 23.37C18.1467 23.79 17.1067 24 16 24Z"
                  fill="#121415"
                />
              </svg>
            </Tooltip>
            <Menu
              id="basic-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              slotProps={{
                list: { "aria-labelledby": "basic-button" },
              }}
            >
              <MenuItem onClick={handleClose}>
                <ListItemIcon>
                  <PersonIcon fontSize="small" /> &nbsp;
                  <button
                    style={{ color: "black", background: "none", border: "none", cursor: "pointer", padding: 0, font: "inherit" }}
                    onClick={handleRouteToUserProfile}
                  >
                    My Profile
                  </button>
                </ListItemIcon>
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" /> &nbsp;{" "}
                  <span style={{ color: "black" }}>Sign Out</span>
                </ListItemIcon>
              </MenuItem>
            </Menu>
          </div>
        </nav>

        {/* Portfolio Change Confirmation Dialog */}
        <Dialog
          open={confirmDialogOpen}
          onClose={handleCancelPortfolioChange}
          aria-labelledby="confirm-dialog-title"
          aria-describedby="confirm-dialog-description"
        >
          <DialogTitle id="confirm-dialog-title">Switch Portfolio</DialogTitle>
          <DialogContent>
            <DialogContentText
              id="confirm-dialog-description"
              sx={{ letterSpacing: "0px" }}
            >
              You are going to switch to {getPortfolioIdValue(selectedId)} portfolio. Are you sure?
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ marginRight: "10px" }}>
            <Button onClick={handleCancelPortfolioChange} color="secondary">
              Cancel
            </Button>
            <Button
              onClick={handleConfirmPortfolioChange}
              color="primary"
              autoFocus
              sx={{ maxWidth: "160px !important" }}
            >
              Switch
            </Button>
          </DialogActions>
        </Dialog>
      </header>
    )
  );
}
